<script setup lang="ts">
import { computed, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import ColumnFilterMenu from "@/ui/components/FileBrowser/ColumnFilterMenu.vue";
import { useFileBrowserActions } from "@/ui/components/FileBrowser/useFileBrowserActions";
import { canStartDrag } from "@/ui/components/FileBrowser/moveRules";
import TagChip from "@/ui/components/FileBrowser/TagChip.vue";
import {
	FileColumn,
	FileSortKey,
	ROW_HEIGHT,
	columnLabel,
} from "@/ui/components/FileBrowser/columns";
import {
	DateFilterRange,
	FilterPatch,
	ResourceTreeCategory,
} from "@/ui/components/FileBrowser/types";
import {
	FileTableRow,
	PageLoadStatus,
	rowKey,
} from "@/ui/components/FileBrowser/useFileTable";
import FileDTO = App.Data.FileDTO;
import FileTagDTO = App.Data.FileTagDTO;
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;

const props = defineProps<{
	columns: FileColumn[];
	rows: FileTableRow[];
	resourceTreeCategory: ResourceTreeCategory;
	sortKey: FileSortKey;
	sortDescending: boolean;
	isRowSelected: (row: FileTableRow) => boolean;
	isLoadingPage: boolean;
	/** Awaited by the scroller, so a failed page can be retried rather than lost. */
	loadMore: () => Promise<PageLoadStatus>;
	lastLoadStatus: PageLoadStatus | null;
	locationOf: (file: FileDTO) => string;
	dateFilterValues: Record<string, DateFilterRange>;
	modifiedByUserId: number | null;
	tagIds: number[];
	isDropTarget: (folder: ResourceTreeNodeDO) => boolean;
}>();

const { resourceTreeCategory } = toRefs(props);

const { t } = useI18n();
const { hasAction } = useFileBrowserActions();

/**
 * Dragging is offered only where it could actually land: the consumer has to
 * have enabled the matching update action, and the row itself has to be one the
 * move rules allow. A dialog that browses without granting them gets a table
 * that simply does not drag, rather than one that fails on drop.
 */
const isRowDraggable = (row: FileTableRow): boolean => {
	if (row.kind === "folder") {
		return (
			hasAction("update-folder") &&
			canStartDrag({ files: [], folders: [row.folder] })
		);
	}

	return (
		hasAction("update-file-meta") &&
		canStartDrag({ files: [row.file], folders: [] })
	);
};

const emit = defineEmits<{
	sort: [key: FileSortKey];
	"open-folder": [folder: ResourceTreeNodeDO];
	"select-row": [index: number, modifiers: { ctrl: boolean; shift: boolean }];
	"open-file": [file: FileDTO];
	filter: [key: string, patch: FilterPatch];
	"open-column-menu": [target: [number, number]];
	"open-actions-menu": [target: [number, number], row: FileTableRow];
	"drag-row": [event: DragEvent, row: FileTableRow];
	"drag-over-row": [event: DragEvent, folder: ResourceTreeNodeDO];
	"drag-leave-row": [folder: ResourceTreeNodeDO];
	"drop-on-row": [event: DragEvent, folder: ResourceTreeNodeDO];
	"drag-end": [];
}>();

const filterKind = (key: string): "date" | "user" | "tag" | null => {
	if (key === "last_modified_by") return "user";
	if (key === "tags") return "tag";
	if (key in props.dateFilterValues) return "date";

	return null;
};

const dateRange = (key: string): DateFilterRange =>
	props.dateFilterValues[key] ?? { from: null, to: null };

const fileIcon = computed(() =>
	resourceTreeCategory.value === "dashboards"
		? "fa: fa-regular fa-file-spreadsheet"
		: "mdi-file-chart-outline"
);

/** Two fit in the column; the rest collapse into a +N the tooltip spells out. */
const MAX_VISIBLE_TAGS = 2;

const visibleTags = (file: FileDTO): FileTagDTO[] =>
	(file.tags ?? []).slice(0, MAX_VISIBLE_TAGS);

const hiddenTags = (file: FileDTO): FileTagDTO[] =>
	(file.tags ?? []).slice(MAX_VISIBLE_TAGS);

const isReadOnly = (file: FileDTO): boolean =>
	!!file.active_user_abilities && !file.active_user_abilities.includes("*");

const rowClick = (index: number, event: MouseEvent): void => {
	emit("select-row", index, {
		ctrl: event.ctrlKey || event.metaKey,
		shift: event.shiftKey,
	});
};

const rowDoubleClick = (row: FileTableRow): void => {
	if (row.kind === "folder") {
		emit("open-folder", row.folder);

		return;
	}

	emit("open-file", row.file);
};

const rowRightClick = (
	index: number,
	row: FileTableRow,
	event: MouseEvent
): void => {
	if (!props.isRowSelected(row)) {
		emit("select-row", index, { ctrl: false, shift: false });
	}

	emit("open-actions-menu", [event.clientX, event.clientY], row);
};

const rowDragStart = (
	index: number,
	row: FileTableRow,
	event: DragEvent
): void => {
	if (!isRowDraggable(row)) {
		event.preventDefault();

		return;
	}

	if (!props.isRowSelected(row)) {
		emit("select-row", index, { ctrl: false, shift: false });
	}

	emit("drag-row", event, row);
};

/**
 * The scroller is told what really happened: "error" stops it re-firing at a
 * failing endpoint and shows the retry below, instead of looping silently.
 */
const onLoad = async ({
	done,
}: {
	done: (status: PageLoadStatus) => void;
}): Promise<void> => {
	done(await props.loadMore());
};
</script>

<template>
	<v-infinite-scroll
		class="details-scroll"
		mode="intersect"
		:empty-text="''"
		@load="onLoad"
	>
		<v-table class="details-table" density="compact">
			<thead>
				<tr
					@contextmenu.prevent="
						emit('open-column-menu', [$event.clientX, $event.clientY])
					"
				>
					<th
						v-for="column in columns"
						:key="column.key"
						:style="{ width: column.width, textAlign: column.align ?? 'start' }"
						:class="{ 'is-sortable': !!column.sortKey }"
						@click="column.sortKey && emit('sort', column.sortKey)"
					>
						{{ columnLabel(column) }}
						<span
							v-if="column.sortKey && column.sortKey === sortKey"
							class="sort-arrow"
							:class="{ 'sort-arrow--descending': sortDescending }"
						>
							<v-icon size="11">fa: fa-solid fa-arrow-up</v-icon>
						</span>
						<column-filter-menu
							v-if="filterKind(column.key)"
							:kind="filterKind(column.key)!"
							:from="dateRange(column.key).from"
							:to="dateRange(column.key).to"
							:user-id="modifiedByUserId"
							:tag-ids="tagIds"
							@change="emit('filter', column.key, $event)"
						/>
					</th>
				</tr>
			</thead>

			<tbody>
				<tr
					v-for="(row, index) in rows"
					:key="rowKey(row)"
					class="details-row"
					:class="{
						'details-row--selected': isRowSelected(row),
						'details-row--drop-target':
							row.kind === 'folder' && isDropTarget(row.folder),
					}"
					:style="{ height: `${ROW_HEIGHT}px` }"
					:draggable="isRowDraggable(row)"
					@click="rowClick(index, $event)"
					@dblclick="rowDoubleClick(row)"
					@contextmenu.prevent="rowRightClick(index, row, $event)"
					@dragstart="rowDragStart(index, row, $event)"
					@dragend="emit('drag-end')"
					@dragenter="
						row.kind === 'folder' && emit('drag-over-row', $event, row.folder)
					"
					@dragover="
						row.kind === 'folder' && emit('drag-over-row', $event, row.folder)
					"
					@dragleave="
						row.kind === 'folder' && emit('drag-leave-row', row.folder)
					"
					@drop="
						row.kind === 'folder' && emit('drop-on-row', $event, row.folder)
					"
				>
					<td
						v-for="column in columns"
						:key="column.key"
						:style="{ textAlign: column.align ?? 'start' }"
					>
						<!-- Name -->
						<template v-if="column.key === 'title'">
							<div class="name-cell">
								<v-icon
									v-if="row.kind === 'folder'"
									size="15"
									class="name-cell__icon"
								>
									fa: fa-duotone fa-folder
								</v-icon>
								<v-icon
									v-else
									size="15"
									class="name-cell__icon"
									:color="row.file.is_favorite ? 'warning' : undefined"
								>
									{{ fileIcon }}
								</v-icon>

								<span class="name-cell__label">
									{{ row.kind === "folder" ? row.folder.name : row.file.title }}
								</span>

								<v-icon
									v-if="row.kind === 'file' && isReadOnly(row.file)"
									size="10"
									class="name-cell__badge"
								>
									fa: fa-solid fa-lock
									<v-tooltip activator="parent">
										{{
											t("fileBrowser.ownedBy", {
												name:
													row.file.user?.name ?? t("fileBrowser.anotherUser"),
											})
										}}
									</v-tooltip>
								</v-icon>

								<v-icon
									v-if="row.kind === 'folder'"
									size="10"
									class="name-cell__chevron"
								>
									fa: fa-solid fa-chevron-right
								</v-icon>
							</div>
						</template>

						<!-- Folders carry none of the file metadata -->
						<template v-else-if="row.kind === 'folder'">
							<span class="muted">—</span>
						</template>

						<!-- Tags: the chip carries the colour, sized down to the row -->
						<template v-else-if="column.key === 'tags'">
							<div v-if="row.file.tags?.length" class="tag-cell">
								<span
									v-for="tag in visibleTags(row.file)"
									:key="tag.id"
									class="tag"
								>
									<tag-chip
										:name="tag.name"
										:rgb-color-value="tag.rgb_color_value"
									/>
								</span>

								<span v-if="hiddenTags(row.file).length" class="tag__more">
									+{{ hiddenTags(row.file).length }}
									<v-tooltip activator="parent">
										{{
											hiddenTags(row.file)
												.map((tag) => tag.name)
												.join(", ")
										}}
									</v-tooltip>
								</span>
							</div>
							<span v-else class="muted">—</span>
						</template>

						<template
							v-else-if="column.key === 'mode' || column.key === 'view_tab'"
						>
							<!-- The column translates the enum; the chip only frames it. -->
							<v-chip v-if="row.file.mode ?? row.file.view_tab" size="x-small">
								{{ column.value?.(row.file, { locationOf }) }}
							</v-chip>
							<span v-else class="muted">—</span>
						</template>

						<template v-else>
							{{ column.value?.(row.file, { locationOf }) ?? "—" }}
						</template>
					</td>
				</tr>

				<tr v-if="!rows.length && !isLoadingPage && lastLoadStatus !== 'error'">
					<td :colspan="columns.length">
						<div class="empty">{{ t("fileBrowser.nothingHere") }}</div>
					</td>
				</tr>
			</tbody>
		</v-table>

		<template #loading>
			<v-progress-linear color="primary" height="2" indeterminate />
		</template>

		<template #error="{ props: retryProps }">
			<div class="load-error">
				<span>{{ t("fileBrowser.loadMoreFailed") }}</span>
				<v-btn
					v-bind="retryProps"
					size="small"
					variant="text"
					:text="t('fileBrowser.retry')"
				/>
			</div>
		</template>
	</v-infinite-scroll>
</template>

<style scoped lang="scss">
.details-scroll {
	height: 100%;
}

.details-table {
	:deep(thead th) {
		position: sticky;
		top: 0;
		z-index: 1;
		background: rgb(var(--color-surface));
		font-size: 12px;
		white-space: nowrap;
	}
}

th.is-sortable {
	cursor: pointer;
	user-select: none;
}

th.is-sortable:hover {
	background-color: rgba(var(--color-secondary), 0.6);
}

.sort-arrow {
	display: inline-flex;
	align-items: center;
	margin-left: 4px;
	transition: transform 150ms ease;

	&--descending {
		transform: rotate(180deg);
	}
}

.details-row {
	cursor: default;
	user-select: none;

	&:hover:not(&--selected) {
		background-color: rgba(var(--color-secondary), 0.45);
	}

	&--selected {
		background-color: rgb(var(--color-secondary));
	}

	/* Reads as "into here", so it outlines rather than just tinting - a tint
	   alone is what selection already means in this table. */
	&--drop-target {
		outline: 2px solid rgb(var(--color-primary));
		outline-offset: -2px;
		background-color: rgba(var(--color-primary), 0.08);
	}

	:deep(td) {
		font-size: 13px;
		line-height: 1.4;
		vertical-align: middle;
		/* Equal-width digits, so dates and counts line up column to column. */
		font-variant-numeric: tabular-nums;
		font-variant: tabular-nums;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
}

.name-cell {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;

	&__icon,
	&__badge {
		flex: none;
	}

	&__label {
		flex: 1 1 auto;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__chevron {
		flex: none;
		opacity: 0.4;
	}

	&__badge {
		opacity: 0.5;
	}
}

.tag-cell {
	display: flex;
	align-items: center;
	gap: 4px;
	min-width: 0;
}

.tag {
	display: inline-flex;
	align-items: center;
	min-width: 0;

	&__more {
		flex: none;
		font-size: 11.5px;
		cursor: default;
		color: rgba(var(--color-on-background), 0.7);
	}
}

.muted {
	color: rgba(var(--color-on-background), 0.6);
}

.load-error {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 12px 8px;
	font-size: 13px;
	color: rgba(var(--color-on-background), 0.8);
}

.empty {
	padding: 18px 8px;
	font-size: 13px;
	font-style: italic;
	color: rgba(var(--color-on-background), 0.7);
}
</style>
